import { accessToken$, impersonateEmail$ } from "$root/functional-areas/session/atom";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { readChatSettings } from "./chatSettings";
import { rateLimitedFrom } from "./rateLimit";

export type MCPTool = {
	name: string;
	description?: string;
	inputSchema: Record<string, unknown>;
	/**
	 * Standard MCP tool annotations (behavioural hints for the client UI).
	 */
	annotations?: {
		title?: string;
		readOnlyHint?: boolean;
		destructiveHint?: boolean;
		idempotentHint?: boolean;
		openWorldHint?: boolean;
	};
	/**
	 * Task-augmentation support declared by the server. Long-running mutations
	 * (optimizer runs, portfolio recomputes) advertise `taskSupport: "required"`:
	 * they MUST be called task-augmented — the call returns a task id and the real
	 * result is fetched from the task queue (see {@link isTaskTool}).
	 */
	execution?: {
		taskSupport?: "required" | "optional" | "forbidden";
	};
};

/**
 * Whether a tool is a query (read-only), per the server's `readOnlyHint` annotation.
 */
export function isReadOnlyTool(tool: MCPTool): boolean {
	return tool.annotations?.readOnlyHint === true;
}

/**
 * Whether a tool must be (or may be) invoked as an MCP *task*: the tools/call returns
 * a task id immediately and the terminal result is polled from the task queue, so the
 * model issues a single tool call and never polls a status tool itself.
 */
export function isTaskTool(tool: MCPTool): boolean {
	const support = tool.execution?.taskSupport;
	return support === "required" || support === "optional";
}

type JSONRPCRequest = {
	jsonrpc: "2.0";
	id?: number | string;
	method: string;
	params?: unknown;
};

type JSONRPCResponse = {
	jsonrpc: "2.0";
	id: number | string;
	result?: unknown;
	error?: { code: number; message: string; data?: unknown };
};

const PROTOCOL_VERSION = "2025-03-26";

/**
 * Browser-side MCP client over the Streamable HTTP transport, for tool DISCOVERY only:
 * sphere-mcp executes every tool itself (MDOT-25078), and this tab lists them to wall the
 * panel when the assistant has nothing to work with (chatAvailability.ts) and to know which
 * calls write (botWriteInvalidation.ts).
 *
 * The transport is used in its STATELESS mode, because that is the only mode the
 * server offers: `/mcp` mints no `Mcp-Session-Id`, ignores one a client sends, and
 * answers no `404 session_not_found`. So there is no session to carry, to lose, or
 * to recover — every request stands on its own bearer. Read this before adding
 * anything that wants to be remembered between calls: the server will not remember
 * it, and a restart mid-conversation is a non-event here.
 */
export class MCPHttpClient {
	private nextId = 1;
	private initializePromise: Promise<void> | null = null;

	constructor(private readonly url: string) {}

	async listTools(): Promise<MCPTool[]> {
		await this.ensureInitialized();
		const result = (await this.call("tools/list")) as { tools: MCPTool[] };
		return result.tools ?? [];
	}

	/**
	 * The MCP handshake, run at most once per client and awaited by every method
	 * above.
	 *
	 * Memoised rather than merely flagged: the promise is shared, so a burst of
	 * concurrent tool calls waits on ONE `initialize` instead of opening with one
	 * each. A failure clears the memo, so the next caller retries the handshake
	 * rather than inheriting a rejected promise for the life of the client.
	 *
	 * That is the whole of the lifecycle now. This used to be wrapped in a
	 * `withSession` that caught `404 session_not_found`, dropped the session id and
	 * re-handshaked, because the server kept its transports in memory and a deploy
	 * invalidated every live session. The server is stateless (see the class doc),
	 * so there is nothing to invalidate and nothing to recover: what is left is the
	 * protocol's own opening — it negotiates the revision and tells the server which
	 * client this is — not a handle being kept alive.
	 */
	private ensureInitialized(): Promise<void> {
		if (!this.initializePromise) {
			this.initializePromise = (async () => {
				await this.call("initialize", {
					protocolVersion: PROTOCOL_VERSION,
					capabilities: {},
					clientInfo: { name: "sphere-ai-frontend", version: "0.1.0" },
				});
				await this.notify("notifications/initialized");
			})().catch((err) => {
				this.initializePromise = null;
				throw err;
			});
		}
		return this.initializePromise;
	}

	private async call(method: string, params?: unknown): Promise<unknown> {
		const id = this.nextId++;
		const response = await this.fetchRPC({ jsonrpc: "2.0", id, method, params });
		if (!response) throw new Error(`MCP ${method}: empty response`);
		if (response.error) throw new Error(`MCP ${method}: ${response.error.message}`);
		return response.result;
	}

	private async notify(method: string, params?: unknown): Promise<void> {
		await this.fetchRPC({ jsonrpc: "2.0", method, params });
	}

	private async fetchRPC(body: JSONRPCRequest): Promise<JSONRPCResponse | null> {
		const accessToken = readAtom(accessToken$);
		const headers: Record<string, string> = {
			"Content-Type": "application/json",
			Accept: "application/json, text/event-stream",
		};
		if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
		// While impersonating, keep the impersonator's own bearer token and tag the
		// request so the MCP (and the Sphere calls it makes) resolve as the target
		// user. Read fresh per request so starting/stopping impersonation mid-session
		// takes effect immediately. Mirrors the app's axios interceptor (initAuth).
		const impersonateEmail = readAtom(impersonateEmail$);
		if (impersonateEmail) headers["X-Impersonate-Email"] = impersonateEmail;
		// The user's web-access switch (see chatSettings). It can only narrow: the
		// server ANDs it with its own `WEB_SEARCH_ENABLED` ceiling, so `true` means
		// "don't opt out" rather than "give me web access". Sent on every request (not
		// just initialize) because the server resolves it per request and enforces it
		// twice: it decides which tools THAT request's tools/list advertises, and the
		// web-fetch handler re-reads it per call and refuses.
		//
		// Both halves matter, and for opposite directions of the switch. Unticking it
		// takes effect on the very next call through the per-call refusal, whatever the
		// cached tool list still says. TICKING it needs the advertisement, because a
		// tool the model was never handed is a tool it cannot call — so the caller keys
		// its tool-discovery query on this setting and re-lists when it flips (see
		// SphereAIChatSection). Without that, turning web search on mid-conversation
		// changed nothing and the assistant went on saying it had no web access.
		headers["X-Sphere-Web-Search"] = String(readChatSettings().webSearch);

		const res = await fetch(this.url, {
			method: "POST",
			headers,
			body: JSON.stringify(body),
			credentials: "omit",
		});

		if (res.status === 202 || body.id === undefined) {
			// notification or accepted-without-content
			return null;
		}
		if (!res.ok) {
			// Refused to protect a budget, not a failure of this request: typed so a
			// caller can honour the server's own Retry-After instead of guessing at a
			// backoff. Shared with every other sphere-mcp wire — the budget is per USER,
			// not per endpoint (./rateLimit.ts).
			if (res.status === 429) {
				throw rateLimitedFrom(res);
			}
			// Everything else is an ordinary transport failure. A 404 used to be read
			// here as `session_not_found` and recovered from; it now means what it says
			// — no /mcp at this URL — and needs no special case.
			throw new Error(`MCP HTTP ${res.status} ${res.statusText}`);
		}

		const contentType = res.headers.get("content-type") ?? "";
		if (contentType.includes("text/event-stream")) {
			return await parseSSEResponse(res, body.id);
		}
		return (await res.json()) as JSONRPCResponse;
	}
}

async function parseSSEResponse(res: Response, requestId: number | string): Promise<JSONRPCResponse> {
	if (!res.body) throw new Error("MCP SSE response has no body");
	const reader = res.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	try {
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			buffer += decoder.decode(value, { stream: true });
			const events = buffer.split("\n\n");
			buffer = events.pop() ?? "";
			for (const event of events) {
				for (const line of event.split("\n")) {
					if (!line.startsWith("data:")) continue;
					const data = line.slice(5).trimStart();
					if (!data) continue;
					const parsed = JSON.parse(data) as JSONRPCResponse;
					if (parsed.id === requestId) return parsed;
				}
			}
		}
	} finally {
		reader.releaseLock();
	}
	throw new Error("MCP SSE stream ended without a matching response");
}

/**
 * A tool schema this tab posts on the chat wire — only `open_page`, whose page list lives in
 * this app's router. MCP's own `{ name, description, inputSchema }` vocabulary.
 *
 * CROSS-REPO CONTRACT, kept in sync by hand: read by `openPageTool` in
 * sphere-mcp/src/server/chat/ui-tools.ts.
 */
export type SphereAIChatTool = {
	name: string;
	description?: string;
	inputSchema: Record<string, unknown>;
};
